import { Add } from "iconsax-react";
import { EmptyState } from "@/shared/components";
import { Button, Prompt } from "@/shared/ui";
import { useProjectsPage } from "../../hooks";
import { ProjectFormModal, ProjectRemovalModal } from "../../components";
import {
  ProjectsFilterPanel,
  ProjectGridCard,
  ProjectsToolbar,
  ProjectsPageSkeleton,
} from "./components";

export default function ProjectsPage() {
  const {
    t,
    projects,
    isLoading,
    error,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    selectedCategories,
    handleCategoryToggle,
    selectedRegions,
    handleRegionToggle,
    selectedStatuses,
    handleStatusToggle,
    dateRange,
    setDateRange,
    budgetFrom,
    setBudgetFrom,
    budgetTo,
    setBudgetTo,
    maxBudget,
    handleProjectClick,
    pageTitle,
    selectedProject,
    isEditOpen,
    isCreateOpen,
    waitingForRefetch,
    successAction,
    canEditProject,
    handleEditProject,
    handleCloseEdit,
    handleEditSuccess,
    handleOpenCreate,
    handleCloseCreate,
    handleCreateSuccess,
    handleCloseSuccessPrompt,
    handleResetFilters,
    hasActiveFilters,
    removalProjectId,
    handleRequestRemovalFromEdit,
    handleCloseRemoval,
  } = useProjectsPage();

  if (isLoading) {
    return (
      <>
        <title>{pageTitle}</title>
        <section className="flex-1">
            <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
            </div>
            <ProjectsPageSkeleton />
        </section>
      </>
    );
  }

  if (error) {
    return (
      <>
        <title>{pageTitle}</title>
        <section className="flex-1">
            <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
              <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
            </div>
            <p className="mt-4 text-body-regular-md content-action-negative">{t("page.error")}</p>
        </section>
      </>
    );
  }

  const internalProjects = projects.filter((project) => project.is_internal);
  const externalProjects = projects.filter((project) => !project.is_internal);

  function renderProjectGrid(list: typeof projects) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((project) => (
          <ProjectGridCard
            key={project.id}
            project={project}
            onClick={handleProjectClick}
            onEdit={handleEditProject}
            canEdit={canEditProject}
          />
        ))}
      </div>
    );
  }

  return (
    <>
      <title>{pageTitle}</title>
      <section className="flex-1">
          <div className="flex gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
                <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
                {canEditProject && (
                  <Button variant="primary" size="lg" onClick={handleOpenCreate}>
                    <Add size={16} color="currentColor" />
                    {t("page.createProject")}
                  </Button>
                )}
              </div>

              <ProjectsToolbar totalResults={projects.length} sortBy={sortBy} onSortChange={setSortBy} />

              {projects.length === 0 && (
                <EmptyState
                  hasActiveFilters={hasActiveFilters}
                  noResultsText={t("page.noResults")}
                  noDataText={t("page.noData")}
                  noDataHint={t("page.noDataHint")}
                />
              )}

              {internalProjects.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-heading-sm content-base-primary mb-4">{t("page.internalProjects")}</h2>
                  {renderProjectGrid(internalProjects)}
                </div>
              )}

              {externalProjects.length > 0 && (
                <div>
                  <h2 className="text-heading-sm content-base-primary mb-4">{t("page.externalProjects")}</h2>
                  {renderProjectGrid(externalProjects)}
                </div>
              )}
            </div>

            <ProjectsFilterPanel
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedCategories={selectedCategories}
              onCategoryToggle={handleCategoryToggle}
              selectedRegions={selectedRegions}
              onRegionToggle={handleRegionToggle}
              selectedStatuses={selectedStatuses}
              onStatusToggle={handleStatusToggle}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              budgetFrom={budgetFrom}
              onBudgetFromChange={setBudgetFrom}
              budgetTo={budgetTo}
              onBudgetToChange={setBudgetTo}
              maxBudget={maxBudget}
              onReset={handleResetFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
      </section>

      <ProjectFormModal
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        onSuccess={handleEditSuccess}
        isRefetching={waitingForRefetch}
        project={selectedProject}
        mode="edit"
        onRequestRemoval={handleRequestRemovalFromEdit}
      />

      <ProjectFormModal
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
        onSuccess={handleCreateSuccess}
        isRefetching={waitingForRefetch}
        project={null}
        mode="create"
      />

      {removalProjectId !== null && (
        <ProjectRemovalModal
          projectId={removalProjectId}
          onClose={handleCloseRemoval}
          onCompleted={handleCloseRemoval}
        />
      )}

      {successAction && (
        <Prompt
          onClose={handleCloseSuccessPrompt}
          title={t(`prompt.${successAction}Title`)}
          text={t(`prompt.${successAction}Text`)}
          variant="success"
          namespace="ProjectsPage"
        />
      )}
    </>
  );
}
